import React, { useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { ButtonContinue } from '../../common/Button/ButtonContinue';
import TextInput from '../../common/TextInput/TextInput';
import DateSelector from '../../common/DateSelector/DateSelector';
import {
  isValidName,
  getInsuranceJourneyMinMaxDob,
  InsuranceJourney
} from '../../common/HelperMethods';
import { useContext, useState } from 'react';
import { SiteContext } from '../../../context/SiteContext';
import {
  InsuranceJourneyContext,
  InsuranceJourneyState
} from '../../common/CommonTypes';
import { useOutletContext } from 'react-router-dom';
import { CommonValidation } from '../../common/CommonValidation/commonValidation';
import {
  ValidationConstants,
  ValidationMessages
} from '../../../consts/Common';
import useCarInsuranceController from '../../../hooks/insuranceJourney/useCarInsuranceController';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export type Errors = {
  dateOfBirth: string;
  firstName: string;
  lastName: string;
  email: string;
  postcode: string;
};

const initialErrors = {
  dateOfBirth: '',
  firstName: '',
  lastName: '',
  email: '',
  postcode: ''
};

const SaveForLaterManager = (): JSX.Element => {
  const { site } = useContext(SiteContext);

  const [popupRequired, setPopupRequired] = useState(false);
  const [dobIsValid, setDobIsValid] = useState(true);
  const [updateDobError, setUpdateDobError] = useState(false);
  const [errors, setErrors] = useState<Errors>(initialErrors);

  const { journey, pageEvents } = useOutletContext<InsuranceJourneyContext>();
  const { savingQuote, personalDetails, addressDetails } = journey;
  const { firstName, lastName, email, dateOfBirth } = personalDetails;
  const { overnightParkedAddress } = addressDetails;
  const { personalDetailsPage, addressDetailsPage, commonEvents } = pageEvents;
  const {
    onChangeDateOfBirth,
    onChangeEmailAddress,
    onChangeFirstName,
    onChangeLastName
  } = personalDetailsPage;
  const { onChangeOvernightParkedAddress } = addressDetailsPage;
  const { setSavingQuote, setPageLoading } = commonEvents;

  const { saveQuote } = useCarInsuranceController();

  const { minDob, maxDob } = getInsuranceJourneyMinMaxDob();
  const validationMessages = ValidationMessages.InsuranceJourney;
  const validationConstants = ValidationConstants.Insurance;

  useEffect(() => {
    if (savingQuote) {
      const hasMinimumData =
        firstName &&
        lastName &&
        dateOfBirth &&
        email &&
        overnightParkedAddress.postCode;

      if (hasMinimumData) {
        setPopupRequired(false);
        saveQuote(journey, site.user.token, saveQuoteSuccess, saveQuoteFailure);
      } else {
        setPopupRequired(true);
      }
    }
  }, [savingQuote]);

  const onSaveClick = () => {
    const isValid = validateSaveForLaterForm();

    if (isValid) {
      saveQuote(journey, site.user.token, saveQuoteSuccess, saveQuoteFailure);
    }
  };

  const saveQuoteSuccess = (quote: InsuranceJourneyState) => {
    console.log(JSON.stringify(quote));
    setSavingQuote(false);
    setPageLoading(false);
    toast.success('Quote saved successfully');
  };

  const saveQuoteFailure = (error: string) => {
    console.error(`Failed to save quote: ${error}`);
    setSavingQuote(false);
    setPageLoading(false);
    toast.error('Quote failed to save');
  };

  const validateSaveForLaterForm = () => {
    let isValid = true;

    const error: Errors = {
      ...initialErrors
    };

    if (CommonValidation.isEmpty(firstName)) {
      isValid = false;
      error.firstName = validationMessages.firstnameRequiredMsg;
    } else if (
      !CommonValidation.isValidLength(
        firstName,
        validationConstants.firstNameCharLimit
      )
    ) {
      isValid = false;
      error.firstName = validationMessages.firstNameLimitError;
    } else {
      if (isValidName(firstName)) error.firstName = '';
      else {
        error.firstName = ValidationMessages.InsuranceJourney.errorInvalidName;
        isValid = false;
      }
    }

    if (CommonValidation.isEmpty(lastName)) {
      isValid = false;
      error.lastName = validationMessages.lastNameRequiredMsg;
    } else if (lastName.length < validationConstants.lastNameMinCharLimit) {
      isValid = false;
      error.lastName = validationMessages.lastNameLimitError;
    } else {
      if (isValidName(lastName)) error.lastName = '';
      else {
        error.lastName = ValidationMessages.InsuranceJourney.errorInvalidName;
        isValid = false;
      }
    }

    if (dobIsValid === false) {
      setUpdateDobError(true);
      isValid = false;
      error.dateOfBirth = validationMessages.dobInvalidMsg;
    } else if (CommonValidation.isEmpty(dateOfBirth)) {
      setUpdateDobError(true);
      isValid = false;
      error.dateOfBirth = validationMessages.dobRequiredMsg;
    } else if (InsuranceJourney.isAgeValid(dateOfBirth) === false) {
      setUpdateDobError(true);
      isValid = false;
      error.dateOfBirth = validationMessages.ageOutOfRange;
    }

    if (CommonValidation.isEmpty(overnightParkedAddress.postCode)) {
      error.postcode = 'Postcode is required';
      isValid = false;
    } else if (
      !CommonValidation.isValidPostcode(overnightParkedAddress.postCode ?? '')
    ) {
      error.postcode = 'Postcode is invalid';
      isValid = false;
    }

    if (CommonValidation.isNotEmpty(email)) {
      if (!CommonValidation.isValidEmail(email)) {
        error.email = validationMessages.invalidEmailMsg;
        isValid = false;
      }
    } else {
      isValid = false;
      error.email = validationMessages.emailRequiredMsg;
    }

    setErrors(error);

    return isValid;
  };

  const handleOnChangePostcode = (e: React.FormEvent<HTMLInputElement>) => {
    onChangeOvernightParkedAddress({
      ...overnightParkedAddress,
      postCode: e.currentTarget.value
    });
  };

  const onDobValidated = (isValid: boolean): void => {
    setUpdateDobError(false);
    setDobIsValid(isValid);
  };

  const closePopup = () => {
    setSavingQuote(false);
    setPageLoading(false);
  };

  const showPopup = popupRequired && savingQuote;

  return (
    <>
      <Modal
        role='modal-popup'
        isOpen={showPopup}
        className={`insurance-${site.brand.brandName}`}
        toggle={closePopup}>
        <ModalHeader toggle={closePopup}>Save for later</ModalHeader>
        <ModalBody>
          <div className='modal-subtitle'>
            <h6>
              Fill in the details below to save your application. You can
              retrieve it anytime in the next 30 days
            </h6>
          </div>
          <div className='form-container'>
            <div className='one-col'>
              <div className='control-label'>First name</div>
              <div className='control'>
                <TextInput
                  placeholder='First name'
                  name='firstname'
                  className='input-text w250'
                  value={firstName}
                  onChange={onChangeFirstName}
                  error={errors.firstName}
                />
              </div>
            </div>
            <div className='one-col'>
              <div className='control-label'>Last name</div>
              <div className='control'>
                <TextInput
                  placeholder='Last name'
                  name='lastname'
                  className='input-text w250'
                  value={lastName}
                  onChange={onChangeLastName}
                  error={errors.lastName}
                />
              </div>
            </div>
            <div className='one-col'>
              <div className='control-label'>Date of birth</div>
              <div className='control'>
                <DateSelector
                  value={dateOfBirth}
                  minDate={minDob}
                  maxDate={maxDob}
                  invalidDateMessage={errors.dateOfBirth}
                  updateDobError={updateDobError}
                  culture={site.cultureCode}
                  onChange={(value) => onChangeDateOfBirth(value)}
                  onValidated={(isValid) => onDobValidated(isValid)}
                  name={'dob'}
                />
              </div>
            </div>
            <div className='one-col'>
              <div className='control-label'>Postcode</div>
              <div className='control'>
                <TextInput
                  placeholder='Postcode'
                  name='postcode'
                  className='input-text w250'
                  onChange={handleOnChangePostcode}
                  value={overnightParkedAddress.postCode}
                  error={errors.postcode}
                />
              </div>
            </div>
            <div className='one-col'>
              <div className='control-label'>Email Address</div>
              <div className='control'>
                <TextInput
                  placeholder='Email Address'
                  name='email'
                  className='input-text w250'
                  value={email}
                  onChange={onChangeEmailAddress}
                  error={errors.email}
                />
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <ButtonContinue
            name='modal-btn-continue'
            text='Save my quote'
            onClick={onSaveClick}
            additionalClassNames='btn-ok'
          />
          <ButtonContinue
            name='btn-cancel'
            text='Cancel'
            onClick={closePopup}
            additionalClassNames='btn-cancel'
          />
        </ModalFooter>
      </Modal>
      <ToastContainer hideProgressBar theme='colored' />
    </>
  );
};

export default SaveForLaterManager;
